/*
 *   File : pan-details.js
 *   Author URI : https://evoqins.com
 *   Description :  Component will allow the customer to submit PAN details.
 *   Integrations : null
 *   Version : v1.1
 */

import { useEffect, useState, useContext } from "react"
import { parse, format } from 'date-fns';
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { CustomFilePicker, CustomTextInput, DatePicker, FilePreview } from "../../../../Component/Form-elements"
import { GradientButton } from "../../../../Component/CTA";
import APIService from "../../../../Service/api-service";
import { _getKycProfile } from "../../../../Helper/api";
import { DarkModeContext } from "../../../../Context/dark-mode-context";
import { _uploadDocumentToS3 } from "../../../../Service/aws-service";
import { _isFileTypeAllowed } from "../../../../Helper";
import Store from "../../../../Store";

const NRIPANDetailsPage = (props) => {

    const PAN_DATA = useSelector(state => state.generalData.KYC_PROGRESS_DATA.pan_details);
    const PROGRESS_DATA = useSelector(state => state.generalData.KYC_PROGRESS_DATA);
    const { darkMode } = useContext(DarkModeContext);
    const [panNumber, setPanNumber] = useState(PAN_DATA.pan);
    const [panNumberError, setPanNumberError] = useState("");
    const [dob, setDOB] = useState(null);
    const [dobError, setDobError] = useState("");
    const [loader, setLoader] = useState(false);
    const [name, setName] = useState(PROGRESS_DATA.kra_info.name !== null ? PROGRESS_DATA.kra_info.name : "");
    const [nameError, setNameError] = useState("");
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [uploadedPan, setUploadedPan] = useState(null);
    const [uploadedPanError, setUploadedPanError] = useState("");
    const [fileLoader, setFileLoader] = useState(false);
    const [panUrl, setPanUrl] = useState("");
    const [poi, setPoi] = useState("");
    const [poiError, setPoiError] = useState("");

    useEffect(() => {
        const _handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', _handleResize);

        // Cleanup listener on component unmount
        return () => {
            window.removeEventListener('resize', _handleResize);
        };
    }, []);



    // max date that can be selected for dob (current date - 18 years)
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 18);

    console.log('maxDate', maxDate);


    useEffect(() => {
        if (PAN_DATA.date_of_birth !== null) {
            const parsed_date = parse(PAN_DATA.date_of_birth, 'dd-MM-yyyy', new Date());
            const formatted_date = format(parsed_date, 'dd/MM/yyyy');
            setDOB(formatted_date);
        }
    }, [PAN_DATA])

    useEffect(() => {
        if (PAN_DATA.place_of_incorporation !== null) {
            setPoi(PAN_DATA.place_of_incorporation);
        }
    }, [PAN_DATA])

    useEffect(() => {
        if (PROGRESS_DATA.huf_pan_url !== null) {
            setPanUrl(PROGRESS_DATA.huf_pan_url)
            const decodedURL = decodeURIComponent(decodeURIComponent(PROGRESS_DATA.huf_pan_url));

            // Extract file name from URL

            const fileName = decodedURL.split('?')[0].split("/").pop();
            setUploadedPan(fileName)

        }
    }, [PROGRESS_DATA])

    // handler for input fields
    const _handleInputFields = (type, value, file) => {
        switch (type) {
            case 1:
                setPanNumber(value);
                setPanNumberError("");
                break;
            case 2:
                console.log("value", value)
                setDOB(value);
                setDobError("");
                break;
            case 3:
                // eslint-disable-next-line
                var file_formatted = value.replace(/^.*[\\\/]/, '').split('/');
                file_formatted = file_formatted[0].replace(/[\(\)\s]/g, '');
                const parts = file_formatted.split('.');
                if (parts.length > 1) {
                    const extension = '.' + parts.pop();
                    if (_isFileTypeAllowed(extension)) {
                        // checking if file size is greater than 5mb
                        const file_size = 5 * 1024 * 1024
                        if (file.size < file_size) {
                            _fileUpload(file_formatted, file);
                        } else {
                            setUploadedPanError("File size should be less than 5mb");
                        }
                    }
                    else {
                        setUploadedPanError("File type should be .pdf, .jpeg, .jpg, .png");
                    }
                }
                break
            case 4:
                setName(value);
                setNameError("");
                break;
        }
    }

    const _validate = () => {
        let valid = true;

        if (dob === null) {
            setDobError("Enter date of birth");
            valid = false;
        }
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber)) {
            setPanNumberError("Enter valid pan number");
            valid = false;
        }
        if (name === "") {
            setNameError("Please enter valid name");
            valid = false;
        }
        if (uploadedPan === null && PROGRESS_DATA.onboarding_sub_type === "HUF") {
            setUploadedPanError("Please upload PAN");
            valid = false;
        }
        if (poi === "" && PROGRESS_DATA.onboarding_sub_type === "HUF") {
            setPoiError("Enter Place of Incorporation");
            valid = false;
        }
        if (valid === true) {
            setLoader(true);
            _saveDetails();
        }
    };


    // API - save pan number and dob for kyc
    const _saveDetails = async () => {
        const formatted_date = format(parse(dob, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
        let url = 'kyc/check';
        let payload = JSON.stringify({
            pan: panNumber,
            dob: formatted_date,
            place_of_incorporation: poi,
            name_on_pan: name,
            huf_pan_url: uploadedPan === null ? null : panUrl
        });
        APIService(true, url, payload, "access", true).then((response) => {
            toast.dismiss();
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("PAN details submitted successfully", {
                    type: "success",
                });
                Store.dispatch({
                    type: 'KYC_PROGRESS',
                    payload: response.kyc_data
                });
                props.updateProgress(4, "NRI", props.is_manual);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                });
            }

            setLoader(false);
        });
    }

    //API :: file upload api
    const _fileUpload = (key, file) => {
        setUploadedPanError("");
        setFileLoader(true);
        let url = "general/file-upload";
        let body = {
            "key": `support/${key}`
        }

        APIService(true, url, body).then((response) => {
            if (response.status_code === 200) {
                setFileLoader(true);

                // s3 helper
                _uploadDocumentToS3(response.data, key, file).then((result) => {
                    setPanUrl(response.data.view_info); // file got uploaded successfully
                    setFileLoader(false);
                    setUploadedPan(key);
                }).catch(error => {
                    toast.dismiss();
                    toast.error(error, {
                        type: "error",
                    });
                    setFileLoader(false);
                });

            } else {
                setFileLoader(false);
                toast.dismiss();
                toast.error(response, {
                    type: "error",
                });
            }
        })
    }

    return (
        <div>
            <p className="color-black e-poppins e-poppins-bold e-font-24 mb-2">Enter your PAN details</p>
            <p className="color-outer-space e-poppins-regular e-font-16 line-height-24px mb-0">Submit your PAN card details. This is a regulatory requirement.</p>


            <div className="row margin-34px-top mb-5">
                <div className="col-md-5 padding-62px-right-mob padding-5px-right-mob">
                    <CustomTextInput label="PAN number"
                        postfix="*"
                        onEnter={_validate}
                        pan={true}
                        value={panNumber}
                        maxLength={10}
                        inputClass="text-uppercase"
                        error={panNumberError}
                        className="margin-42px-bottom "
                        handleChange={(value) => _handleInputFields(1, value)} />

                    <CustomTextInput label="Enter you name as in PAN"
                        postfix="*"
                        value={name}
                        error={nameError}
                        onEnter={_validate}
                        className="margin-42px-bottom "
                        handleChange={(value) => _handleInputFields(4, value)} />

                    <DatePicker label={"Enter date of birth as per PAN"}
                        error={dobError}
                        value={dob}
                        isAdult={true}
                        maxDate={PROGRESS_DATA.onboarding_sub_type == "HUF" ? new Date() : maxDate}
                        className="margin-42px-bottom"
                        postFix="*"
                        dob={(value) => _handleInputFields(2, value)} />

                    <GradientButton label="Continue"
                        loading={loader}
                        className="px-3 padding-12px-tb e-font-16 margin-40px-top mob-w-100"
                        handleClick={_validate} />
                </div>
                <div className="col-md-5 padding-5px-right-mob mt-lg-0 mt-md-0 mt-4 d-flex justify-content-end align-items-start">
                    <img src={darkMode === true ? require("../../../../Assets/Images/kyc/pan-image-dark.png") : require("../../../../Assets/Images/kyc/pan-image.png")}
                        alt="Pan"
                        width={screenWidth > 576 ? 414 : 380}
                        draggable={false} />
                </div>
            </div>

        </div>
    )
}

export default NRIPANDetailsPage